// react_frontend/src/Components/Modules/Category/CategoryList.js
import React, { useState, useEffect, useMemo, useContext } from "react";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { MotherContext } from "../../../Context/MotherContext";

// RSuite Components
import {
  Button,
  Input,
  Badge,
  Loader,
  Modal,
  Whisper,
  Tooltip,
  Message,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaTags,
  FaQrcode,
  FaUser,
  FaEye,
  FaToggleOn,
  FaToggleOff,
  FaEdit,
  FaTrashAlt,
  FaPlus,
  FaSearch,
} from "react-icons/fa";

const CategoryList = () => {
  const {
    categories = [],
    startCategoryPolling,
    updateCategoryInList,
    removeCategoryFromList,
  } = useContext(MotherContext);

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const isAdmin = localStorage.getItem("who_am_i") === "1" || localStorage.getItem("who_am_i") === "2";

  useEffect(() => {
    startCategoryPolling();
  }, [startCategoryPolling]);

  // Filtered Data
  const filteredData = useMemo(() => {
    return (categories || []).filter(
      (cat) =>
        cat &&
        (
          cat.category_name?.toLowerCase().includes(search.toLowerCase()) ||
          cat.category_slug?.toLowerCase().includes(search.toLowerCase()) ||
          cat.category_code?.toLowerCase().includes(search.toLowerCase())
        )
    );
  }, [categories, search]);

  // Handle Delete
  const handleDelete = (category) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this category!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await AxiosAuthInstance.delete(`/add_category/${category.id}`);
          removeCategoryFromList(category.id);
          toaster.push(
            <Message type="success" duration={3000}>
              Category deleted successfully!
            </Message>,
            { placement: "topEnd" }
          );
        } catch (err) {
          toaster.push(
            <Message type="error" duration={5000}>
              Failed to delete category.
            </Message>,
            { placement: "topEnd" }
          );
        }
      }
    });
  };

  // Handle Status Toggle
  const handleStatusToggle = (category) => {
    const newStatus = category.category_status === 1 ? 0 : 1;

    Swal.fire({
      title: `Are you sure?`,
      text: `You will mark "${category.category_name}" as ${newStatus ? "Active" : "Inactive"
        }.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${newStatus ? "Activate" : "Deactivate"}`,
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await AxiosAuthInstance.post(
            `update_category_status/${category.id}/${newStatus}`
          );
          updateCategoryInList({ ...category, category_status: newStatus });
          toaster.push(
            <Message type="success" duration={3000}>
              Category status updated!
            </Message>,
            { placement: "topEnd" }
          );
        } catch (err) {
          toaster.push(
            <Message type="error" duration={5000}>
              Failed to update status.
            </Message>,
            { placement: "topEnd" }
          );
        }
      }
    });
  };

  // View Category Details
  const viewCategory = (category) => {
    setSelectedCategory(category);
    setShowModal(true);
  };

  // Columns
  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{index + 1}</strong>,
      width: "60px",
      center: true,
    },
    {
      name: "Category Name",
      selector: (row) => row.category_name,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaTags className="text-primary" />
          <span className="fw-medium">{row.category_name}</span>
        </div>
      ),
    },
    {
      name: "Slug",
      selector: (row) => row.category_slug,
      sortable: true,
      width: "200px",
      cell: (row) => <code className="text-muted small">{row.category_slug}</code>,
    },
    {
      name: "Code",
      selector: (row) => row.category_code,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <Badge color="blue" style={{ fontSize: "0.85em" }}>
          <FaQrcode /> {row.category_code}
        </Badge>
      ),
    },
    {
      name: "Status",
      width: "120px",
      center: true,
      cell: (row) => (
        <Badge
          color={row.category_status ? "green" : "red"}
          content={row.category_status ? "Active" : "Inactive"}
          style={{ fontSize: "0.8em" }}
        />
      ),
    },
    {
      name: "Actions",
      cell: (row) =>
        isAdmin ? (
          <div className="d-flex gap-1 justify-content-center">
            <Whisper placement="top" speaker={<Tooltip>View details</Tooltip>}>
              <Button
                size="sm"
                color="yellow"
                startIcon={<FaEye />}
                onClick={() => viewCategory(row)}
              >
                View
              </Button>
            </Whisper>

            <Whisper
              placement="top"
              speaker={<Tooltip>{row.category_status ? "Deactivate" : "Activate"}</Tooltip>}
            >
              <Button
                size="sm"
                color={row.category_status ? "green" : "red"}
                startIcon={row.category_status ? <FaToggleOn /> : <FaToggleOff />}
                onClick={() => handleStatusToggle(row)}
              >
                {row.category_status ? "Active" : "Inactive"}
              </Button>
            </Whisper>

            <Whisper placement="top" speaker={<Tooltip>Edit category</Tooltip>}>
              <Link to={`/edit_category/${row.id}`} style={{ textDecoration: "none" }}>
                <Button size="sm" color="blue" startIcon={<FaEdit />}>
                  Edit
                </Button>
              </Link>
            </Whisper>

            <Whisper className="d-none" placement="top" speaker={<Tooltip>Delete category</Tooltip>}>
              <Button
                className="d-none"
                size="sm"
                color="red"
                startIcon={<FaTrashAlt />}
                onClick={() => handleDelete(row)}
              >
                Delete
              </Button>
            </Whisper>
          </div>
        ) : (
          <Badge color="default">View Only</Badge>
        ),
      width: "300px",
      center: true,
    },
    {
      name: "Created By",
      selector: (row) => row.creator?.name,
      sortable: true,
      width: "160px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <FaUser className="text-secondary" />
          <small>{row.creator?.name}</small>
        </div>
      ),
    },
  ];

  const customStyles = {
    head: { style: { fontSize: "14px", fontWeight: "bold", backgroundColor: "#f8f9fa" } },
    rows: { style: { fontSize: "14px", minHeight: "60px" } },
    cells: { style: { padding: "12px 8px" } },
  };

  const AddCategoryButton = () =>
    isAdmin ? (
      <Whisper placement="top" speaker={<Tooltip>Add new category</Tooltip>}>
        <Link to="/add_category" style={{ textDecoration: "none" }}>
          <Button
            appearance="primary"
            startIcon={<FaPlus />}
            className="d-flex align-items-center custom-bg"
          >
            Add Category
          </Button>
        </Link>
      </Whisper>
    ) : null;

  return (
    <>
      <Breadcrumb title="Category Management" />

      <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <h5 className="text-dark mb-0">
          <FaTags className="me-2" />
          Product Categories
        </h5>

        <div className="d-flex gap-2 w-100 w-md-auto">
          <Input
            placeholder="Search by name, slug, code..."
            value={search}
            onChange={setSearch}
            prefix={<FaSearch />}
            style={{ width: 300 }}
            size="md"
          />
          <AddCategoryButton />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[50, 100, 200, 500]}
        progressPending={loading}
        progressComponent={
          <div className="d-flex justify-content-center my-5">
            <Loader size="md" /> <span className="ms-2">Loading categories...</span>
          </div>
        }
        noDataComponent={
          <div className="text-center my-5 text-muted">
            <FaTags size={48} className="mb-3 text-secondary" />
            <h5>No categories found</h5>
            <p>Try adjusting your search or add a new category.</p>
          </div>
        }
        highlightOnHover
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        customStyles={customStyles}
        striped
      />

      <Modal open={showModal} onClose={() => setShowModal(false)} size="md">
        <Modal.Header>
          <Modal.Title>
            <FaEye /> Category Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedCategory && (
            <table className="table table-borderless">
              <tbody>
                <tr>
                  <th width="140">Name</th>
                  <td>{selectedCategory?.category_name}</td>
                </tr>
                <tr>
                  <th>Slug</th>
                  <td>
                    <code>{selectedCategory?.category_slug}</code>
                  </td>
                </tr>
                <tr>
                  <th>Code</th>
                  <td>
                    <Badge color="blue">{selectedCategory?.category_code}</Badge>
                  </td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>
                    <Badge
                      color={selectedCategory?.category_status ? "green" : "red"}
                      content={selectedCategory?.category_status ? "Active" : "Inactive"}
                    />
                  </td>
                </tr>
                <tr>
                  <th>Created By</th>
                  <td>{selectedCategory?.creator?.name}</td>
                </tr>
                <tr>
                  <th>Updated By</th>
                  <td>
                    {selectedCategory?.updator?.name || (
                      <small className="text-muted">Not updated yet</small>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Created At</th>
                  <td>
                    <small>{selectedCategory?.created_at}</small>
                  </td>
                </tr>
                <tr>
                  <th>Updated At</th>
                  <td>
                    <small>
                      {selectedCategory?.updated_at !== selectedCategory?.created_at
                        ? selectedCategory?.updated_at
                        : "Not updated yet"}
                    </small>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowModal(false)} appearance="subtle">
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CategoryList;
